import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight } from 'lucide-react';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { formatDate } from '../utils/dateUtils';
import { toast } from 'react-hot-toast';
import { XMLParser } from 'fast-xml-parser';
import { useState, useEffect } from 'react';

interface BlogPost {
  title: string;
  link: string;
  pubDate: string;
  description: string;
  content: string;
  thumbnail?: string;
  enclosure?: {
    link: string;
    type: string;
  };
  'content:encoded'?: string;
  'content:encodedSnippet'?: string;
}

// Function to extract first image from HTML content
const extractImageFromContent = (content: string): string | null => {
  if (!content) return null;
  
  // Try to find img tags in the content
  const imgRegex = /<img[^>]+src\s*=\s*["']([^"']+)["'][^>]*>/i;
  const match = content.match(imgRegex);
  
  if (match && match[1]) {
    return match[1];
  }
  
  // Try to find images in figure tags (common in Substack)
  const figureRegex = /<figure[^>]*>.*?<img[^>]+src\s*=\s*["']([^"']+)["'][^>]*>.*?<\/figure>/is;
  const figureMatch = content.match(figureRegex);
  
  if (figureMatch && figureMatch[1]) {
    return figureMatch[1];
  }
  
  return null;
};

// Function to get post image with fallbacks
const getPostImage = (post: BlogPost): string => {
  // First try thumbnail from RSS
  if (post.thumbnail) {
    return post.thumbnail;
  }
  
  // Try enclosure (for media attachments)
  if (post.enclosure?.link && post.enclosure.type?.startsWith('image/')) {
    return post.enclosure.link;
  }
  
  // Try to extract from content
  const contentImage = extractImageFromContent(post.content || post['content:encoded'] || '');
  if (contentImage) {
    return contentImage;
  }
  
  // Try to extract from description
  const descriptionImage = extractImageFromContent(post.description || '');
  if (descriptionImage) {
    return descriptionImage;
  }
  
  // Fallback to a default image
  return 'https://images.unsplash.com/photo-1497366811353-6870744d04b2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80';
};

// Function to clean description text
const cleanDescription = (description: string): string => {
  if (!description) return '';
  
  // Remove HTML tags
  const withoutHtml = description.replace(/<[^>]*>/g, '');
  // Decode HTML entities
  const withoutEntities = withoutHtml
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ');
  
  return withoutEntities.trim();
};

export function BlogPage() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const RSS_URL = 'https://api.rss2json.com/v1/api.json?rss_url=https://calculatedmoves.substack.com/feed';
  const SUBSTACK_URL = 'https://calculatedmoves.substack.com'; // Fixed typo: was caculatedmoves

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await fetch(RSS_URL);
        
        if (!response.ok) {
          throw new Error('Failed to fetch blog posts');
        }
        
        const data = await response.json();
        
        // Check if we have valid items
        if (data?.items?.length) {
          console.log('First post data:', data.items[0]); // Debug log
          setPosts(data.items);
        } else {
          console.error('No posts found in feed');
          setPosts([]);
        }
      } catch (error) {
        console.error('Error fetching blog posts:', error);
        toast.error('Failed to load blog posts');
        setPosts([]); // Set empty state on error
      } finally {
        setIsLoading(false);
      }
    };

    fetchPosts();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#ddd9d9] py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        </div>
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="min-h-screen bg-[#ddd9d9] py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center py-12">
            <h2 className="text-2xl font-bold text-black mb-4">No Posts Yet</h2>
            <p className="text-black">Check back soon for new content!</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#ddd9d9] py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-black">Latest Blog Posts</h1>
          <p className="mt-2 text-black">Insights and updates from Resa Sue</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {posts.slice(0, isMobile ? 3 : 8).map((post, index) => (
            <article
              key={index}
              className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all"
            >
              <div className="relative h-64">
                <img
                  src={getPostImage(post)}
                  alt={post.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Fallback if image fails to load
                    const target = e.target as HTMLImageElement;
                    target.src = 'https://images.unsplash.com/photo-1497366811353-6870744d04b2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80';
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              </div>

              <div className="p-6">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-black" />
                    <span className="text-sm text-black">
                      {formatDate(post.pubDate || (post as any).published)}
                    </span>
                  </div>
                </div>

                <h2 className="text-xl font-semibold text-black mb-2">
                  {post.title}
                </h2>
                <p className="text-black mb-4">
                  {cleanDescription(post.description || '').substring(0, 150)}
                  {cleanDescription(post.description || '').length > 150 ? '...' : ''}
                </p>
                <div className="flex justify-between items-center">
                  <a 
                    href={post.link || "https://calculatedmoves.substack.com"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-[#800020] hover:opacity-80 group"
                  >
                    Read on Substack
                    <ArrowRight className="h-4 w-4 ml-1 text-black group-hover:translate-x-1 transition-transform" />
                  </a>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="text-center mt-8">
          <a
            href={SUBSTACK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-6 py-3 bg-[#800020] text-white rounded-xl hover:opacity-90 transition-colors"
          >
            View More Posts on Substack
          </a>
        </div>
      </div>
    </div>
  );
}